// Filename: DataBIRequestsPage.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import MiniBot from "../../MiniBot";
import DataBIRequestsTable from "./DataBIRequestsTable"; // Import the new table

// Interface for search options
interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

// Interface for the processed request data structure
interface ProcessedDataBIRequest {
    id: string;
    serviceName: string;
    applicationName?: string;
    status: string;
    createdAt: string | null;
    actionedAt: string | null;
    ownerName: string | null | undefined;
    ownerAvatar?: string;
    approverName: string | null | undefined;
    approverAvatar?: string;
    reasonForRejection?: string;
}

const DataBIRequestsPage: React.FC = () => {
    const [requests, setRequests] = useState<ProcessedDataBIRequest[]>([]);
    const [searchOptions, setSearchOptions] = useState<SearchOptions[]>([]);
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                // GraphQL Query to get Data & BI services
                const data = await queryGraphql(`
                    query GetDataAnalytics {
                      getDataAnalytics {
                        id
                        name
                        application {
                           id
                           applicationName
                        }
                        status
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          googleProfilePicture
                        }
                        solutionApprover {
                          id
                          name
                          googleProfilePicture
                        }
                      }
                    }
                `);

                const rawRequestsData = data.getDataAnalytics || [];

                // Process the raw data into the structure needed for the requests view
                const processedRequestsArr: ProcessedDataBIRequest[] = rawRequestsData.map((req: any) => ({
                    id: req.id,
                    serviceName: req.name,
                    applicationName: req.application?.applicationName,
                    status: req.status ? req.status.toLowerCase() : 'pending',
                    createdAt: req.createdAt ? new Date(Number(req.createdAt)).toISOString() : null,
                    actionedAt: req.updatedAt ? new Date(Number(req.updatedAt)).toISOString() : null,
                    ownerName: req.solutionOwner?.name,
                    ownerAvatar: req.solutionOwner?.googleProfilePicture,
                    approverName: req.solutionApprover?.name,
                    approverAvatar: req.solutionApprover?.googleProfilePicture,
                    reasonForRejection: req.reasonForRejection,
                }));
                setRequests(processedRequestsArr);

                // Generate search options from the processed data
                let generatedOptions: SearchOptions[] = processedRequestsArr
                    .map((req: ProcessedDataBIRequest) => {
                        const options = [
                            {
                                category: "serviceName",
                                groupBy: "SERVICE NAME",
                                chipLabel: `Service: ${req.serviceName}`,
                                dropdownLabel: req.serviceName,
                            },
                        ];
                        if (req.applicationName) {
                            options.push({
                                category: "applicationName",
                                groupBy: "APPLICATION",
                                chipLabel: `App: ${req.applicationName}`,
                                dropdownLabel: req.applicationName,
                            });
                        }
                        if (req.ownerName) {
                            options.push({
                                category: "ownerName",
                                groupBy: "OWNER",
                                chipLabel: `Owner: ${req.ownerName}`,
                                dropdownLabel: req.ownerName,
                            });
                        }
                        if (req.approverName) {
                            options.push({
                                category: "approverName",
                                groupBy: "APPROVER",
                                chipLabel: `Approver: ${req.approverName}`,
                                dropdownLabel: req.approverName,
                            });
                        }
                        if (req.status) {
                            options.push({
                                category: "status",
                                groupBy: "STATUS",
                                chipLabel: `Status: ${req.status.charAt(0).toUpperCase() + req.status.slice(1)}`,
                                dropdownLabel: req.status.charAt(0).toUpperCase() + req.status.slice(1),
                            });
                        }
                        return options;
                    })
                    .flat();

                // Deduplicate search options
                generatedOptions = generatedOptions.filter(
                    (obj, index, self) =>
                        obj.dropdownLabel &&
                        index === self.findIndex((t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel)
                );

                // Sort search options
                generatedOptions.sort((a, b) => {
                    if (a.groupBy > b.groupBy) return 1;
                    if (a.groupBy < b.groupBy) return -1;
                    if (a.dropdownLabel > b.dropdownLabel) return 1;
                    if (a.dropdownLabel < b.dropdownLabel) return -1;
                    return 0;
                });
                setSearchOptions(generatedOptions);

            } catch (error) {
                console.log("Error fetching Data BI requests:", error);
            }
        };

        fetchData();
    }, []);

    // Filter requests based on the search query
    const filteredRequests =
        searchQuery.length === 0
            ? requests
            : requests.filter(
                (req: ProcessedDataBIRequest) =>
                    (searchQuery.filter((e) => e.category === "serviceName").length > 0
                        ? searchQuery
                                .filter((e) => e.category === "serviceName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(req.serviceName?.toLowerCase())
                        : true) &&
                    (searchQuery.filter((e) => e.category === "applicationName").length > 0
                        ? (req.applicationName &&
                            searchQuery
                                .filter((e) => e.category === "applicationName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(req.applicationName.toLowerCase()))
                        : true) &&
                    (searchQuery.filter((e) => e.category === "ownerName").length > 0
                        ? (req.ownerName &&
                            searchQuery
                                .filter((e) => e.category === "ownerName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(req.ownerName.toLowerCase()))
                        : true
                    ) &&
                    (searchQuery.filter((e) => e.category === "approverName").length > 0
                        ? (req.approverName &&
                            searchQuery
                                .filter((e) => e.category === "approverName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(req.approverName.toLowerCase()))
                        : true
                    ) &&
                    (searchQuery.filter((e) => e.category === "status").length > 0
                        ? searchQuery
                                .filter((e) => e.category === "status")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(req.status?.toLowerCase())
                        : true)
            );

    // Group filtered requests by status for accordions
    const pendingStatuses = ['pending', 'submitted', 'provisioning'];
    const activeStatuses = ['active', 'approved', 'success'];
    const rejectedStatuses = ['rejected', 'failed'];

    const filteredPendingRequests = filteredRequests.filter(
        (req: ProcessedDataBIRequest) => pendingStatuses.includes(req.status)
    );
    const filteredActiveRequests = filteredRequests.filter(
        (req: ProcessedDataBIRequest) => activeStatuses.includes(req.status)
    );
    const filteredRejectedRequests = filteredRequests.filter(
        (req: ProcessedDataBIRequest) => rejectedStatuses.includes(req.status)
    );

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-data-bi"} />
            <MiniBot />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginY: 1,
                                marginLeft: 2,
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
                                Requests
                            </Typography>
                             <IconButton
                                aria-label="Help"
                                onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                sx={{
                                    padding: 0, border: 'none',  mr:2,
                                    '&:hover': { backgroundColor: 'action.hover' },
                                }}
                              >
                                <Box component="img" src="/assets/Help.svg" alt="Help Icon" sx={{ width: 40, height: 40, display: 'block' }} />
                            </IconButton>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}
                        >
                            <Box>
                                <Typography
                                    fontFamily="Gilroy-SemiBold"
                                    fontSize="16px"
                                >{`DATA ANALYTICS & BI (${requests.length})`}</Typography>
                                <Typography
                                    fontFamily="Gilroy-Regular"
                                    fontSize="14px"
                                >{`View all the requests raised for Data Analytics & BI services`}</Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) => (
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
                                    ))
                                )}
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by service, owner, status etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <img
                                                            src="/assets/search-normal.svg"
                                                            alt="Search Icon"
                                                            style={{ width: "16px", paddingLeft: "4px" }}
                                                        />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: {
                                                backgroundColor: "#f4f4f4",
                                                minHeight: "45px",
                                                width: "500px",
                                                borderRadius: "8px",
                                                fontFamily: "Gilroy-Regular",
                                                fontSize: "14px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion
                            defaultExpanded={filteredPendingRequests.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredPendingRequests.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }}/>
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending BI Tools ({filteredPendingRequests.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredPendingRequests.length > 0 && (
                                    <DataBIRequestsTable requests={filteredPendingRequests} />
                                )}
                            </AccordionDetails>
                        </Accordion>
                        
                        <Accordion
                            defaultExpanded={true}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredActiveRequests.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active BI Tools ({filteredActiveRequests.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredActiveRequests.length > 0 && (
                                    <DataBIRequestsTable requests={filteredActiveRequests} />
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={filteredRejectedRequests.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredRejectedRequests.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected BI Tools ({filteredRejectedRequests.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredRejectedRequests.length > 0 && (
                                    <DataBIRequestsTable requests={filteredRejectedRequests} />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default DataBIRequestsPage;